import React from "react";
import { LooseRadioInput, PlainInputWrapper } from "../__Internal__";
import { useRadioGroupState } from "./RadioGroupStateContext";

type Props = {
  /**
   * The value of the radio button. Is not selectable when `null`.
   */
  value: string | null;
  /**
   * The title text.  Used as the label for the radio input.
   */
  title: React.ReactNode;
  /**
   * The subtitle text.  Used as the label for the radio input.
   */
  subtitle?: React.ReactNode;
  /**
   * The icon to render on the left side of the radio button.
   * @default null
   */
  icon?: string;
  /**
   * Whether the radio button is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Testing utility tag.
   * @default "radio-input"
   */
  "data-testid"?: string;
};

export const RadioInput: React.FC<Props> = (props) => {
  const { value, title, subtitle, icon = null, disabled = false } = props;
  const testId = props["data-testid"] ?? "radio-input";

  const inputRef = React.useRef<HTMLInputElement>(null);
  const { name, required, selected, onChange, onInvalid, radio } = useRadioGroupState();

  const checked = selected === value;
  const id = `${name}-${value}`;

  return (
    <PlainInputWrapper
      ref={inputRef}
      id={id}
      value={value}
      title={title}
      subtitle={subtitle}
      icon={icon}
      checked={checked}
      disabled={disabled}
    >
      {(childProps) => {
        return (
          <LooseRadioInput
            {...childProps}
            state={radio}
            onChange={onChange}
            onInvalid={onInvalid}
            type="radio"
            name={name}
            required={required}
            aria-required={required}
            data-testid={testId}
          />
        );
      }}
    </PlainInputWrapper>
  );
};

RadioInput.displayName = "RadioInput";
